/* akk-bok-shop.js: the shoppable layer of the flip-book (sections/akk-bok.liquid).
 *
 *   Hotspots  "Hnappar í bók" blocks (sections/akk-bok-hnappar.liquid, edited in the
 *             theme editor) list the buttons: product, page, and the marker's centre
 *             as percentages of the page image. With no such blocks on the page the
 *             generated assets/akk-bok-hotspots.json is used instead: boxes as
 *             fractions ({"pages": {"6": [{handle, x, y, w, h, name}]}}) whose
 *             top-right corner gets the marker. Either way the "+" button sits inside
 *             the page's .akk-bok__sheet, so it turns with the paper (no extra layer,
 *             no shadow, nothing re-rasterised per frame).
 *   Editor    in the theme editor, selecting a block flips to its page and lights its
 *             marker, a changed block redraws, and a readout follows the pointer with
 *             x/y in the block's units.
 *   Panel     a marker opens the box in a modal <dialog>: side panel on wide
 *             screens, bottom sheet on phones. Product data is /products/<h>.js,
 *             fetched once per handle. The add control is akk-quote.js's own
 *             <akk-quote-add>; the quantity field writes its data-qty.
 *   Contents  "Efnisyfirlit": page thumbnails grouped by chapter (rendered in
 *             Liquid) and a search over box names, chapters and, once fetched,
 *             product titles and tags.
 * The book itself is driven through root.akkBok (assets/akk-bok.js).
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-akk-bok]');
  const book = root?.akkBok;
  if (!root || !book) return;

  let S = {};
  try {
    S = JSON.parse(root.querySelector('[data-akk-bok-strings]')?.textContent || '{}');
  } catch { /* defaults below */ }
  const t = (key, fallback) => S[key] || fallback;

  const $ = (sel, scope = root) => scope.querySelector(sel);
  const panel = $('[data-akk-bok-panel]');
  const toc = $('[data-akk-bok-toc]');
  const vatDiv = 100 + (Number.parseInt(root.dataset.akkVat ?? '', 10) || 24);
  const routeRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  /* ---------- small helpers ---------- */

  /* Same money and ex-VAT sums as akk-quote.js / akk-price-vsk. */
  const group = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const money = (cents) => `${group(cents / 100)} kr.`;
  const low = (() => {
    try { return JSON.parse(document.getElementById('akk-vsk-low')?.textContent || 'null'); } catch { return null; }
  })();
  const lowIds = new Set(Array.isArray(low?.ids) ? low.ids.map(String) : []);
  const lowDiv = 100 + (Number.isFinite(low?.rate) ? low.rate : 11);
  const printed = low?.print && typeof low.print === 'object' ? low.print : {};
  /* A printed item shows the print's own figures; the rest are derived as in akk-quote.js. */
  const unitPrice = (data) => {
    const [printEx, printInc] = printed[data.handle] ?? [0, 0];
    const inc = printInc ? printInc * 100 : data.price;
    const ex = printEx ? printEx * 100 : Math.round(inc / (lowIds.has(String(data.id)) ? lowDiv : vatDiv)) * 100;
    return { inc, ex };
  };

  /* "ÍTALSKA BOXIÐ #2" -> "Ítalska boxið #2" */
  const nice = (s) => {
    const low = String(s || '').trim().toLocaleLowerCase('is');
    return low.charAt(0).toLocaleUpperCase('is') + low.slice(1);
  };
  /* Search key: lower case, accents folded, so "italska" finds "Ítalska". */
  const fold = (s) => String(s || '').toLocaleLowerCase('is')
    .replace(/ð/g, 'd').replace(/þ/g, 'th').replace(/æ/g, 'ae').replace(/ö/g, 'o')
    .normalize('NFD').replace(/[̀-ͯ]/g, '');

  const sized = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  /* Product descriptions open with "X ER AKKÚRAT FYRIR..." / "...alla sem": one sentence. */
  const plain = (html) => {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').replace(/(…|\.\.\.)\s*(…|\.\.\.)/g, ' ').trim();
  };
  const clip = (s, max) => {
    if (s.length <= max) return s;
    const cut = s.slice(0, max);
    return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), max - 20))}…`;
  };

  /* ---------- chapters (from the contents markup) ---------- */

  const chapters = [...root.querySelectorAll('[data-akk-bok-chapter]')]
    .map((el) => ({ name: el.dataset.akkBokChapter, page: Number(el.dataset.page) }))
    .sort((a, b) => a.page - b.page);
  const chapterOf = (page) => {
    let hit = null;
    for (const c of chapters) if (c.page <= page) hit = c;
    return hit;
  };

  /* ---------- product data, cached per handle ---------- */

  const products = new Map();
  /* Search keys (title + tags, folded) for the products that have arrived; the
     search reads this synchronously so a box name answers at once and the
     product words join on the rerun after loadExtras. */
  const productIndex = new Map();
  const product = (handle) => {
    if (!products.has(handle)) {
      products.set(handle, fetch(`${routeRoot}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null)).catch(() => null).then((p) => {
        if (p) productIndex.set(handle, fold(`${p.title} ${(p.tags || []).join(' ')}`));
        return p;
      }));
    }
    return products.get(handle);
  };

  /* ---------- hotspots ---------- */

  /** @type {{handle:string,name:string,page:number,x:number,y:number,w:number,h:number,point?:boolean,blockId?:string,text?:string,el?:HTMLButtonElement}[]} */
  const spots = [];
  let spotsLoad = null;

  const sheetFor = (page) => root.querySelector(`[data-akk-bok-page][data-page="${page}"] [data-akk-bok-sheet]`);
  const frac = (v) => Math.min(1, Math.max(0, Number(v) || 0));

  /* The blocks on the page, if any: one point per button. */
  const blockSpots = () => {
    const out = [];
    for (const li of document.querySelectorAll('[data-akk-hnappur]')) {
      const page = Number.parseInt(li.dataset.page, 10);
      const handle = li.dataset.handle;
      if (!handle || !(page >= 1 && page <= book.count)) continue;
      out.push({
        handle,
        page,
        name: li.dataset.label || nice(li.dataset.title || handle.replace(/-/g, ' ')),
        x: frac(Number(li.dataset.x) / 100),
        y: frac(Number(li.dataset.y) / 100),
        w: 0,
        h: 0,
        point: true,
        blockId: `${li.closest('.shopify-section')?.id || ''}:${li.dataset.blockId || ''}`,
        text: li.dataset.text || '',
      });
    }
    return out;
  };

  const clearSpots = () => {
    for (const s of spots) s.el?.remove();
    spots.length = 0;
    root.removeAttribute('data-has-spots');
  };

  const loadSpots = () => {
    if (spotsLoad) return spotsLoad;
    const fromBlocks = blockSpots();
    if (fromBlocks.length) {
      spots.push(...fromBlocks);
      renderSpots();
      spotsLoad = Promise.resolve(spots);
      return spotsLoad;
    }
    spotsLoad = (S.spots ? fetch(S.spots).then((r) => (r.ok ? r.json() : null)) : Promise.resolve(null))
      .catch(() => null)
      .then((data) => {
        if (!data || typeof data.pages !== 'object') return spots;
        /* A file made for another set of page images would put the boxes on the wrong pages. */
        if (data.source && S.prefix && data.source !== S.prefix) {
          console.warn(`akk-bok: hotspots are for "${data.source}", the book is "${S.prefix}"; not shown.`);
          return spots;
        }
        for (const [key, list] of Object.entries(data.pages)) {
          const page = Number.parseInt(key, 10);
          if (!(page >= 1 && page <= book.count) || !Array.isArray(list)) continue;
          for (const s of list) {
            if (!s || typeof s.handle !== 'string' || !s.handle) continue;
            spots.push({
              handle: s.handle,
              name: nice(s.name || s.handle.replace(/-/g, ' ')),
              page,
              x: frac(s.x), y: frac(s.y), w: frac(s.w), h: frac(s.h),
            });
          }
        }
        renderSpots();
        return spots;
      });
    return spotsLoad;
  };

  const renderSpots = () => {
    const open = t('open', 'opna');
    for (const s of spots) {
      const sheet = sheetFor(s.page);
      if (!sheet) continue;
      const layer = sheet.querySelector('.akk-bok__spots') || sheet.appendChild(Object.assign(
        document.createElement('div'), { className: 'akk-bok__spots' }));
      const b = document.createElement('button');
      b.type = 'button';
      b.className = s.point ? 'akk-bok__spot akk-bok__spot--point' : 'akk-bok__spot';
      b.setAttribute('aria-label', `${s.name}, ${open}`);
      b.setAttribute('aria-haspopup', 'dialog');
      /* A point sits where the block says; a box gets its top-right corner, kept inside the page. */
      b.style.setProperty('--spot-x', String(s.point ? s.x : Math.min(s.x + s.w, 1)));
      b.style.setProperty('--spot-y', String(s.y));
      b.innerHTML = '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14"><path d="M7 1.5v11M1.5 7h11" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';
      b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openPanel(s, b); });
      /* Warm the data while the pointer is on its way. */
      b.addEventListener('pointerenter', () => product(s.handle), { once: true });
      b.addEventListener('focus', () => product(s.handle), { once: true });
      s.el = b;
      layer.append(b);
    }
    root.toggleAttribute('data-has-spots', spots.length > 0);
  };

  /* Load the boxes when the browser is idle; the book comes first. */
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 600));
  idle(() => loadSpots(), { timeout: 2500 });

  /* ---------- theme editor: the blocks drive the book ---------- */

  if (window.Shopify?.designMode) {
    let selectedBlock = '';
    const light = () => {
      for (const s of spots) s.el?.classList.toggle('is-found', Boolean(selectedBlock) && s.blockId === selectedBlock);
    };
    const rebuild = () => {
      clearSpots();
      spotsLoad = null;
      loadSpots();
      light();
    };
    const isButtons = (e) => Boolean(e.target?.querySelector?.('[data-akk-bok-hnappar]') || e.target?.matches?.('[data-akk-bok-hnappar]'));
    /* The editor re-renders a section after every setting change; the old markup is
       still in the DOM when unload fires, so the rebuild waits a tick either way. */
    document.addEventListener('shopify:section:load', (e) => { if (isButtons(e)) setTimeout(rebuild, 0); });
    document.addEventListener('shopify:section:unload', (e) => { if (isButtons(e)) setTimeout(rebuild, 0); });
    document.addEventListener('shopify:block:select', (e) => {
      const li = e.target?.closest?.('[data-akk-hnappur]');
      if (!li) return;
      selectedBlock = `${li.closest('.shopify-section')?.id || ''}:${li.dataset.blockId || ''}`;
      const s = spots.find((x) => x.blockId === selectedBlock);
      if (s && !book.shown().includes(s.page)) book.go(s.page);
      light();
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      if (e.target?.closest?.('[data-akk-hnappur]')) { selectedBlock = ''; light(); }
    });

    /* x/y under the pointer, in the block's units (whole percents: a range may have at most 101 steps). */
    const xy = document.createElement('output');
    xy.className = 'akk-bok__xy';
    xy.hidden = true;
    xy.setAttribute('aria-hidden', 'true');
    root.append(xy);
    const whole = (v) => String(Math.round(v));
    root.addEventListener('pointermove', (e) => {
      const sheet = e.target?.closest?.('[data-akk-bok-sheet]');
      if (!sheet) { xy.hidden = true; return; }
      const r = sheet.getBoundingClientRect();
      if (!r.width || !r.height) { xy.hidden = true; return; }
      const px = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
      const py = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
      const page = sheet.closest('[data-akk-bok-page]')?.dataset.page || '';
      xy.textContent = `${t('pageAbbr', 'bls.')} ${page} · x ${whole(px)} · y ${whole(py)}`;
      xy.style.left = `${e.clientX + 14}px`;
      xy.style.top = `${e.clientY + 14}px`;
      xy.hidden = false;
    });
    root.addEventListener('pointerleave', () => { xy.hidden = true; });
  }

  /* ---------- dialogs ---------- */

  let returnFocus = null;
  const showDialog = (dlg, from) => {
    returnFocus = from || document.activeElement;
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
  };
  const closeDialog = (dlg) => {
    if (dlg.open && typeof dlg.close === 'function') dlg.close();
    else dlg.removeAttribute('open');
  };
  for (const dlg of [panel, toc]) {
    if (!dlg) continue;
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg || e.target.closest('[data-akk-bok-dismiss]')) closeDialog(dlg);
    });
    dlg.addEventListener('close', () => {
      const back = returnFocus;
      returnFocus = null;
      if (back && back.isConnected && !dlg.dataset.keepFocus) back.focus({ preventScroll: true });
      delete dlg.dataset.keepFocus;
    });
  }

  /* ---------- the box panel ---------- */

  const P = panel && {
    media: $('[data-akk-bok-p-media]', panel),
    img: $('[data-akk-bok-p-img]', panel),
    where: $('[data-akk-bok-p-where]', panel),
    title: $('[data-akk-bok-p-title]', panel),
    price: $('[data-akk-bok-p-price]', panel),
    ex: $('[data-akk-bok-p-ex]', panel),
    inc: $('[data-akk-bok-p-inc]', panel),
    stock: $('[data-akk-bok-p-stock]', panel),
    status: $('[data-akk-bok-p-status]', panel),
    desc: $('[data-akk-bok-p-desc]', panel),
    actions: $('[data-akk-bok-p-actions]', panel),
    qtyRow: $('[data-akk-bok-p-qtyrow]', panel),
    qty: $('[data-akk-bok-p-qty]', panel),
    add: $('[data-akk-bok-p-add]', panel),
    link: $('[data-akk-bok-p-link]', panel),
  };
  let openToken = 0;

  const qtyValue = () => Math.max(1, Number.parseInt(P.qty.value, 10) || 1);
  const syncQty = () => {
    const adder = P.add.querySelector('akk-quote-add');
    if (adder) adder.dataset.qty = String(qtyValue());
  };
  /* A box already on the list is edited on the list: the button says so and
     goes there (akk-quote-add's own rule), and the quantity field steps aside. */
  const syncInList = () => requestAnimationFrame(() => {
    const inList = Boolean(P.add.querySelector('.is-in-list'));
    P.qtyRow.hidden = inList;
  });

  if (P) {
    panel.addEventListener('click', (e) => {
      const step = e.target.closest('[data-akk-bok-step]');
      if (!step) return;
      P.qty.value = String(Math.max(1, qtyValue() + Number(step.dataset.akkBokStep)));
      syncQty();
    });
    P.qty.addEventListener('input', syncQty);
    P.qty.addEventListener('change', () => { P.qty.value = String(qtyValue()); syncQty(); });
    document.addEventListener('akk:quote:change', () => { if (panel.open) syncInList(); });
  }

  const fillPanel = (s, data) => {
    const chapter = chapterOf(s.page);
    P.where.textContent = `${t('pageAbbr', 'bls.')} ${s.page}${chapter ? ` · ${chapter.name}` : ''}`;
    P.title.textContent = data ? data.title : s.name;
    P.status.textContent = '';
    if (!data) {
      P.img.hidden = true;
      P.media.hidden = true;
      P.price.hidden = true;
      P.stock.hidden = true;
      P.desc.textContent = s.text || '';
      P.actions.hidden = true;
      P.status.textContent = t('error', 'Þetta box fannst ekki í vefversluninni.');
      return;
    }
    const media = (data.media || []).find((m) => m.media_type === 'image') || null;
    const src = media?.src || data.featured_image;
    P.media.hidden = !src;
    if (src) {
      const ratio = media?.aspect_ratio || (media?.width && media?.height ? media.width / media.height : 0);
      P.media.style.aspectRatio = ratio ? String(ratio) : '';
      P.img.hidden = true;
      P.img.onload = () => { P.img.hidden = false; };
      P.img.src = sized(src, 720);
      P.img.srcset = `${sized(src, 480)} 480w, ${sized(src, 720)} 720w, ${sized(src, 960)} 960w`;
      P.img.sizes = '(min-width: 760px) 400px, 100vw';
    }
    const pre = data.price_varies ? `${t('from', 'frá')} ` : '';
    const unit = unitPrice(data);
    P.ex.textContent = `${t('priceEx', 'Verð án vsk.')} ${pre}${money(unit.ex)}`;
    P.inc.textContent = `${t('priceInc', 'Verð með vsk.')} ${pre}${money(unit.inc)}`;
    P.price.hidden = false;
    P.stock.hidden = Boolean(data.available);
    /* A block's own text wins over the shop's description. */
    P.desc.textContent = s.text || clip(plain(data.description), 240);

    const url = data.url || `${routeRoot}products/${data.handle}`;
    P.link.href = url;
    P.qty.value = '1';
    const adder = document.createElement('akk-quote-add');
    adder.className = 'akk-bok-panel__adder';
    Object.assign(adder.dataset, {
      id: String(data.id),
      title: data.title,
      url,
      image: sized(data.featured_image, 160),
      price: String(data.price),
      qty: '1',
      varies: String(Boolean(data.price_varies)),
      listUrl: S.listUrl || '',
      labelAdd: t('addLabel', 'Setja í tilboðslista'),
      labelIn: t('inListLabel', 'Í tilboðslista'),
    });
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'akk-btn akk-btn--primary akk-bok-panel__addbtn';
    const label = document.createElement('span');
    label.dataset.akkAddLabel = '';
    label.textContent = t('addLabel', 'Setja í tilboðslista');
    btn.append(label);
    adder.append(btn);
    P.add.replaceChildren(adder);
    P.actions.hidden = false;
    syncInList();
  };

  const openPanel = async (s, from) => {
    if (!P) return;
    const token = ++openToken;
    P.where.textContent = '';
    P.title.textContent = s.name;
    P.media.hidden = true;
    P.price.hidden = true;
    P.stock.hidden = true;
    P.desc.textContent = '';
    P.actions.hidden = true;
    P.status.textContent = t('loading', 'Sæki upplýsingar…');
    if (!panel.open) showDialog(panel, from);
    const data = await product(s.handle);
    if (token !== openToken) return;
    fillPanel(s, data);
  };

  /* ---------- the contents overlay ---------- */

  const C = toc && {
    open: $('[data-akk-bok-tocopen]'),
    q: $('[data-akk-bok-q]', toc),
    live: $('[data-akk-bok-q-live]', toc),
    results: $('[data-akk-bok-results]', toc),
    list: $('[data-akk-bok-results-list]', toc),
    none: $('[data-akk-bok-q-none]', toc),
    groups: $('[data-akk-bok-groups]', toc),
  };

  const markCurrent = () => {
    if (!C) return;
    const shown = new Set(book.shown());
    for (const b of C.groups.querySelectorAll('[data-akk-bok-goto]')) {
      if (shown.has(Number(b.dataset.akkBokGoto))) b.setAttribute('aria-current', 'page');
      else b.removeAttribute('aria-current');
    }
  };

  /* A new visit to the contents starts from the full grid. */
  const resetSearch = () => {
    if (!C) return;
    searchToken += 1;
    C.q.value = '';
    C.groups.hidden = false;
    C.results.hidden = true;
    C.list.replaceChildren();
    C.live.textContent = '';
  };

  const jump = (page, spot) => {
    toc.dataset.keepFocus = '1';
    closeDialog(toc);
    resetSearch();
    for (const s of spots) s.el?.classList.remove('is-found');
    requestAnimationFrame(() => {
      book.go(page);
      if (spot?.el) {
        spot.el.classList.add('is-found');
        /* Focus the box once its page is on screen (after the turn). */
        setTimeout(() => spot.el.isConnected && spot.el.focus({ preventScroll: true }), 1250);
      } else {
        root.querySelector('[data-akk-bok-next]')?.focus({ preventScroll: true });
      }
    });
  };

  /* Product titles and tags join the search as they arrive. */
  let extrasLoad = null;
  let extrasDone = false;
  const loadExtras = () => {
    if (extrasLoad) return extrasLoad;
    extrasLoad = loadSpots().then(async (list) => {
      const handles = [...new Set(list.map((s) => s.handle))];
      let i = 0;
      const worker = async () => {
        while (i < handles.length) {
          const h = handles[i++];
          await product(h);
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, handles.length) }, worker));
    });
    return extrasLoad;
  };
  const productKey = (handle) => productIndex.get(handle) || '';

  const resultRow = (label, page, sub, onPick) => {
    const li = document.createElement('li');
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'akk-bok-toc__result';
    const name = document.createElement('span');
    name.className = 'akk-bok-toc__rname';
    name.textContent = label;
    const where = document.createElement('span');
    where.className = 'akk-bok-toc__rpage';
    where.textContent = ` · ${t('pageAbbr', 'bls.')} ${page}`;
    b.append(name, where);
    if (sub) {
      const s = document.createElement('span');
      s.className = 'akk-bok-toc__rsub';
      s.textContent = sub;
      b.append(s);
    }
    b.addEventListener('click', onPick);
    li.append(b);
    return li;
  };

  let searchToken = 0;
  const search = async () => {
    const raw = C.q.value.trim();
    const q = fold(raw);
    const token = ++searchToken;
    C.groups.hidden = Boolean(q);
    C.results.hidden = !q;
    if (!q) { C.live.textContent = ''; return; }
    if (!extrasDone) {
      loadExtras().then(() => {
        extrasDone = true;
        if (token === searchToken) search();
      });
    }
    await loadSpots();
    const words = q.split(/\s+/).filter(Boolean);
    const hit = (key) => words.every((w) => key.includes(w));

    const rows = [];
    for (const c of chapters) {
      if (hit(fold(c.name))) rows.push(resultRow(c.name, c.page, '', () => jump(c.page)));
    }
    const seen = new Set();
    for (const s of spots) {
      const id = `${s.handle}@${s.page}`;
      if (seen.has(id)) continue;
      const key = `${fold(s.name)} ${productKey(s.handle)}`;
      if (!hit(key)) continue;
      seen.add(id);
      rows.push(resultRow(s.name, s.page, chapterOf(s.page)?.name || '', () => jump(s.page, s)));
    }
    if (/^\d+$/.test(raw) && Number(raw) >= 1 && Number(raw) <= book.count) {
      const page = Number(raw);
      rows.unshift(resultRow(`${t('pageAbbr', 'bls.')} ${page}`.trim(), page, chapterOf(page)?.name || '', () => jump(page)));
      rows[0].querySelector('.akk-bok-toc__rpage').remove();
    }
    if (token !== searchToken) return;
    C.list.replaceChildren(...rows);
    C.none.hidden = rows.length > 0;
    const n = rows.length;
    C.live.textContent = `${n} ${n === 1 ? t('resultOne', 'niðurstaða') : t('resultMany', 'niðurstöður')}`;
  };

  if (C && C.open) {
    C.open.hidden = false;
    C.open.addEventListener('click', () => {
      markCurrent();
      showDialog(toc, C.open);
      loadSpots();
      const cur = C.groups.querySelector('[aria-current="page"]');
      cur?.scrollIntoView({ block: 'center' });
      /* No keyboard pop-up on phones: focus the search only with a fine pointer. */
      if (matchMedia('(pointer: fine)').matches) C.q.focus({ preventScroll: true });
    });
    C.groups.addEventListener('click', (e) => {
      const b = e.target.closest('[data-akk-bok-goto]');
      if (b) jump(Number(b.dataset.akkBokGoto));
    });
    let timer = 0;
    C.q.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(search, 120); });
    C.q.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        C.list.querySelector('button')?.click();
      }
    });
  }

  /* A box found by search stays marked until the reader moves on. */
  root.addEventListener('akk:bok:page', (e) => {
    const pages = e.detail?.pages || [];
    for (const s of spots) if (s.el && !pages.includes(s.page)) s.el.classList.remove('is-found');
  });
})();
